import { useMemo } from "react";

import __ from "web/Layout/Translations";

import { dayMilliseconds } from "web/constants/date";
import orderCodeStatus from "web/constants/orderCodeStatus";
import productStatusCodes from "web/constants/productCodeStatus";

import { ProductCodeStatus } from "web/types/Code";
import { OrderStatus } from "web/types/Order";

interface ICodeItemStatus {
  switchable?: boolean;
  disabled?: boolean;
  label?: CodeItemStatusLabel;
  isCanceled?: boolean;
  active?: boolean;
  isExpired?: boolean | "";
  statusCode: CodeItemStatusCode;
}

const enum CodeItemStatusLabel {
  CANCELED = "Anulowany",
  PENDING = "Przygotowujemy Twój kod",
  EXPIRED = "Przeterminowany",
  CAN_BE_USED = "Do wykorzystania",
  IS_USED = "Wykorzystany",
  WAITING_FOR_PAYMENT = "Oczekuje na płatność",
  TO_WITHDRAW = "Zgłoszono odstąpienie",
  WITHDRAWING = "W trakcie zwrotu",
  WITHDRAWN = "Odstąpiono od zakupu",
  WITHDRAWN_ERROR = "Błąd zwrotu",
}

export const enum CodeItemStatusCode {
  AVAILABLE = "available",
  USED = "used",
  EXPIRED = "expired",
  CANCELED = "canceled",
  WAITING = "waiting",
  WAITING_FOR_PAYMENT = "waiting_for_payment",
  TO_WITHDRAW = "to_withdraw",
  WITHDRAWING = "withdrawing",
  WITHDRAWN = "withdrawn",
  WITHDRAWN_ERROR = "withdrawn_error",
}

export const inactiveStatusCodes = [
  CodeItemStatusCode.CANCELED,
  CodeItemStatusCode.EXPIRED,
  CodeItemStatusCode.USED,
  CodeItemStatusCode.TO_WITHDRAW,
  CodeItemStatusCode.WITHDRAWING,
  CodeItemStatusCode.WITHDRAWN,
  CodeItemStatusCode.WITHDRAWN_ERROR,
];

const useCodeItemStatus = (
  productStatus: ProductCodeStatus,
  isAvailable: boolean,
  expirationDate: string,
  orderStatus: OrderStatus = "" as OrderStatus
): ICodeItemStatus =>
  useMemo(() => {
    const isExpired =
      expirationDate &&
      (Date.parse(expirationDate.replace(/-/g, "/")) || 0) + dayMilliseconds <
        Date.now();
    const { canceled, canceledError, claimFailed, reservationFailed } =
      productStatusCodes;
    const {
      canceled: orderCanceled,
      canceledError: orderCanceledError,
      canceledMissingPayment: orderCanceledMissingPayment,
      toCancel: orderToCancel,
      rollBackError: orderRollbackError,
      rollBack: orderRollback,
      rolledBack: orderRolledBack,
      toManuallyCancel: orderToManuallyCancel,
      manuallyCancelError: orderManuallyCancelError,
      claimSuccess: orderClaimSuccess,
      partiallyCancelled: orderPartiallyCancelled,
      partiallyWithdrawn: orderPartiallyWithdrawn,
      partiallyWithdrawnAndCancelled: orderPartiallyWithdrawnAndCancelled,
    } = orderCodeStatus;

    // CANCELED
    if (
      [canceled, canceledError, reservationFailed, claimFailed].includes(
        productStatus
      ) ||
      [
        orderCanceled,
        orderCanceledError,
        orderCanceledMissingPayment,
        orderToCancel,
        orderRollbackError,
        orderRollback,
        orderRolledBack,
        orderToManuallyCancel,
        orderManuallyCancelError,
      ].includes(orderStatus)
    ) {
      return {
        disabled: true,
        label: __(CodeItemStatusLabel.CANCELED) as CodeItemStatusLabel,
        isCanceled: true,
        active: false,
        isExpired,
        statusCode: CodeItemStatusCode.CANCELED,
      };
    }

    // EXPIRED / USED
    if (isExpired) {
      return {
        disabled: false,
        label: (isAvailable
          ? __(CodeItemStatusLabel.EXPIRED)
          : __(CodeItemStatusLabel.IS_USED)) as CodeItemStatusLabel,
        isCanceled: false,
        active: false,
        isExpired,
        statusCode: isAvailable
          ? CodeItemStatusCode.EXPIRED
          : CodeItemStatusCode.USED,
      };
    }

    // TO WITHDRAW
    if (productStatus === productStatusCodes.toWithdraw) {
      return {
        disabled: true,
        label: __(CodeItemStatusLabel.TO_WITHDRAW) as CodeItemStatusLabel,
        isCanceled: false,
        active: false,
        isExpired,
        statusCode: CodeItemStatusCode.TO_WITHDRAW,
      };
    }

    // WITHDRAWING / WITHDRAWN (statuses merged per customer request)
    if (
      productStatus === productStatusCodes.withdrawn ||
      productStatus === productStatusCodes.withdrawing
    ) {
      return {
        disabled: true,
        label: __(CodeItemStatusLabel.WITHDRAWN) as CodeItemStatusLabel,
        isCanceled: false,
        active: false,
        isExpired,
        statusCode: CodeItemStatusCode.WITHDRAWN,
      };
    }

    // WITHDRAWN ERROR
    if (productStatus === productStatusCodes.withdrawnError) {
      return {
        disabled: true,
        label: __(CodeItemStatusLabel.WITHDRAWN_ERROR) as CodeItemStatusLabel,
        isCanceled: false,
        active: false,
        isExpired,
        statusCode: CodeItemStatusCode.WITHDRAWN_ERROR,
      };
    }

    // CAN BE USED / USED
    if (
      productStatus === productStatusCodes.claimSuccess &&
      [
        orderClaimSuccess,
        orderPartiallyCancelled,
        orderPartiallyWithdrawn,
        orderPartiallyWithdrawnAndCancelled,
      ].includes(orderStatus)
    ) {
      return {
        switchable: true,
        disabled: false,
        label: (isAvailable
          ? __(CodeItemStatusLabel.CAN_BE_USED)
          : __(CodeItemStatusLabel.IS_USED)) as CodeItemStatusLabel,
        isCanceled: false,
        active: isAvailable,
        isExpired,
        statusCode: isAvailable
          ? CodeItemStatusCode.AVAILABLE
          : CodeItemStatusCode.USED,
      };
    }

    // WAITING FOR PAYMENT

    if (orderStatus === orderCodeStatus.waitingForPayment) {
      return {
        disabled: true,
        label: __(
          CodeItemStatusLabel.WAITING_FOR_PAYMENT
        ) as CodeItemStatusLabel,
        isCanceled: false,
        active: isAvailable,
        isExpired,
        statusCode: CodeItemStatusCode.WAITING_FOR_PAYMENT,
      };
    }

    // PENDING
    return {
      disabled: true,
      label: __(CodeItemStatusLabel.PENDING) as CodeItemStatusLabel,
      isCanceled: false,
      active: isAvailable,
      isExpired,
      statusCode: CodeItemStatusCode.WAITING,
    };
  }, [productStatus, isAvailable, expirationDate, orderStatus]);

export default useCodeItemStatus;
